import { useEffect, useMemo, useCallback } from 'react';
import type { FormField } from '@elfsight/widget-form-builder/widget';
import {
  Form as FormContainer,
  useFormDispatch,
  useFormSelector
} from '@elfsight/widget-form-builder/widget';
import { useLingui } from '@lingui/react/macro';
import { useSettings } from '@wu/widget-core';
import { meta } from '../../meta';
import { useDatetime } from '../datetime/datetime-provider';
import { useCreateCalendarEventMutation } from '../google-calendar/use-create-calendar-event.mutation';
import { usePaymentData } from '../payment/payment-data-provider';
import { BOOKING_HIDDEN_FIELDS, EMAIL_ID } from './fields';
import { FormLayout } from './form-layout';
import { useBookingHiddenFields } from './use-booking-hidden-fields';

function useDisplayedFields() {
  const { shouldUsePayment } = usePaymentData();
  const { defaultFields, fields: customFields } = useSettings();

  return useMemo(() => {
    return [...BOOKING_HIDDEN_FIELDS, ...defaultFields, ...customFields].reduce<
      FormField[]
    >((acc, field) => {
      if (field.id === EMAIL_ID && shouldUsePayment) {
        acc.push({
          ...field,
          required: true,
          visible: true
        });

        return acc;
      }

      if (field.visible === false) {
        return acc;
      }

      acc.push(field);

      return acc;
    }, []);
  }, [shouldUsePayment, defaultFields, customFields]);
}

export function Form() {
  const { t } = useLingui();

  const {
    widgetToken,
    integrationSettingsID,
    afterBookingAction,
    afterBookingRedirectUrl,
    editMode
  } = useSettings();

  const { mutateAsync: createGCEventAsync } = useCreateCalendarEventMutation();
  const { setSelectedTimeslot } = useDatetime();
  const { status } = useFormSelector();
  const dispatch = useFormDispatch();

  useBookingHiddenFields();

  const displayedFields = useDisplayedFields();

  const isSubmittedOrPending = status === 'success' || status === 'pending';

  const resetFormState = useCallback(() => {
    dispatch({ type: 'reset' });
    setSelectedTimeslot(undefined);
  }, [dispatch, setSelectedTimeslot]);

  // Очистка при изменении режима после бронирования (конфигуратор)
  useEffect(() => {
    if (status === 'success' && editMode) {
      resetFormState();
    }
  }, [status, editMode, resetFormState]);

  // Очистка при возврате на страницу кнопкой "Назад" (bfcache)
  useEffect(() => {
    const handlePageShow = (event: PageTransitionEvent) => {
      if (event.persisted && isSubmittedOrPending) {
        resetFormState();
      }
    };

    window.addEventListener('pageshow', handlePageShow);

    return () => {
      window.removeEventListener('pageshow', handlePageShow);
    }
  }, [isSubmittedOrPending, resetFormState]);

  const onFormSubmit = async () => {
    await createGCEventAsync();

    if (afterBookingAction === 'redirect' && afterBookingRedirectUrl) {
      window.open(afterBookingRedirectUrl, '_self');
    }
  };

  const formSettings = useMemo(() => {
    return {
      fields: displayedFields,
      integrationSettingsID,
      widgetToken,
      webhooks: [],
      submitAdditionalData: {
        type: meta.appAlias
      }
    };
  }, [displayedFields, integrationSettingsID, widgetToken]);

  return (
    <FormContainer
      {...formSettings}
      onSubmitSuccessful={onFormSubmit}
      consentValues={[t`Yes`, t`No`]}
    >
      <FormLayout
        displayedFields={displayedFields}
        formSettings={formSettings}
      />
    </FormContainer>
  );
}

/*
  I18N FIX
 */

// Form Builder:
// t(`This field is required`)
